import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Sheet } from '@/components/common/Sheet';
import type { AmicoSfida } from '@/hooks/useMyBoards';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { latoCheTira } from '@/lib/myBoards/riga';
import type {
  BoardSide,
  BoardView,
  ColoreSfidante,
  ModalitaTempoSfida,
  ScenarioDifference,
  ScenarioView,
} from '@/types/myBoards';

/** Cio' che il foglio consegna a chi lo apre: la posizione, il colore di chi sfida, il tempo (gameModeKey del server) e l'amico. */
export interface SfidaDaBoard {
  positionId: string;
  color: ColoreSfidante;
  gameModeKey: string;
  friendUserId: string;
}

const COLORI: { value: ColoreSfidante; key: string }[] = [
  { value: 'W', key: 'myBoards.sheet.white' },
  { value: 'B', key: 'myBoards.sheet.black' },
  { value: 'random', key: 'myBoards.sheet.random' },
];

const DIFFERENZA: Record<ScenarioDifference, string> = {
  roll_removed: 'myBoards.sheet.diff.rollRemoved',
  offer_removed: 'myBoards.sheet.diff.offerRemoved',
  cube_off: 'myBoards.sheet.diff.cubeOff',
  score_not_played: 'myBoards.sheet.diff.scoreNotPlayed',
};

function Pallino({ lato }: { lato: BoardSide }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block h-4 w-4 rounded-full border', lato === 'W' ? 'border-border bg-fg' : 'border-fg-muted bg-bg')}
    />
  );
}

/**
 * «Sfida un amico da qui» (T6): un foglio dal basso sulla scheda del board. Chi sfida sceglie il COLORE (Bianco, Nero o a caso:
 * scegliere il colore e' scegliere un lato della posizione, e il lato di turno tira per primo), il TEMPO — l'unica opzione di
 * gioco — e l'amico. Prima di mandare vede le DIFFERENZE dello scenario (ADR-0191 §6: niente tiro, niente offerta, cubo
 * spento, punteggio non giocato), quelle vere per quel board.
 *
 * Il tempo e' una modalita' del SERVER (`match-modes` a 1 punto): a schermo va la sua etichetta (L-2523), mai parole locali, e
 * «senza orologio» sulla sfida di piattaforma non c'e'.
 * ⚠️ T0: «Invia sfida» CONSEGNA la scelta a chi apre il foglio e non scrive niente (la sfida con i due campi e' il T2, e
 * dipende da Q4, G1 e P1). Con la flag spenta il foglio non si raggiunge.
 */
export function SfidaDaBoardSheet({
  board,
  scenario,
  amici,
  modalita,
  onInvia,
  onClose,
}: {
  board: BoardView;
  scenario: ScenarioView;
  amici: AmicoSfida[];
  modalita: ModalitaTempoSfida[];
  onInvia: (sfida: SfidaDaBoard) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const [colore, setColore] = useState<ColoreSfidante>('random');
  const [tempo, setTempo] = useState<string | null>(modalita[0]?.gameModeKey ?? null);
  const [amico, setAmico] = useState<string | null>(null);
  const [cerca, setCerca] = useState('');

  const { position } = board;
  const tira = latoCheTira(position);
  const visibili = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    return q ? amici.filter((a) => a.nome.toLowerCase().includes(q)) : amici;
  }, [amici, cerca]);

  const pronto = amico !== null && tempo !== null;

  return (
    <Sheet open onClose={onClose} label={t('myBoards.sheet.title')}>
      <div className="flex flex-col gap-4 px-4 pb-2 pt-3" data-testid="board-sfida-foglio">
        <div>
          <h2 className="text-xl font-bold text-fg">
            {t('myBoards.sheet.title')}
          </h2>
          <p className="mt-1 text-sm text-fg-muted">{t('myBoards.sheet.subtitle')}</p>
        </div>

        <section aria-label={t('myBoards.sheet.colorTitle')}>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-fg-muted">{t('myBoards.sheet.colorTitle')}</h3>
          <div role="group" aria-label={t('myBoards.sheet.colorTitle')} className="card-edge flex gap-1 rounded-2xl bg-bg p-1">
            {COLORI.map((c) => {
              const on = colore === c.value;
              return (
                <Button
                  key={c.value}
                  variant="scelta"
                  forma="tessera"
                  tono="incavo"
                  peso="bold"
                  pressed={on}
                  data-testid={`sfida-colore-${c.value}`}
                  onClick={() => {
                    if (on) return;
                    haptics.selection();
                    setColore(c.value);
                  }}
                  // A 320 px la colonna e' di ~93 px: pallino + parola devono stare senza ellissi (misurato nel banco del T0).
                  className="min-h-11 min-w-0 flex-1 gap-1.5 px-1 text-[13px] min-[360px]:gap-2 min-[360px]:text-sm"
                >
                  {c.value !== 'random' && <Pallino lato={c.value} />}
                  <span className="truncate">{t(c.key)}</span>
                </Button>
              );
            })}
          </div>
          <p data-testid="sfida-chi-tira" className="mt-2 text-sm text-fg-muted">
            {t('myBoards.sheet.whoRolls', { side: t(`myBoards.side.${tira}`) })}
          </p>
        </section>

        <section aria-label={t('myBoards.sheet.timeTitle')}>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-fg-muted">{t('myBoards.sheet.timeTitle')}</h3>
          <div role="group" aria-label={t('myBoards.sheet.timeTitle')} className="flex flex-wrap gap-2">
            {modalita.map((m) => {
              const on = tempo === m.gameModeKey;
              return (
                <Button
                  key={m.gameModeKey}
                  variant="scelta"
                  forma="pastiglia"
                  tono="tenue"
                  pressed={on}
                  data-testid={`sfida-tempo-${m.gameModeKey}`}
                  onClick={() => {
                    haptics.selection();
                    setTempo(m.gameModeKey);
                  }}
                  className="min-h-11 px-4 text-sm"
                >
                  {m.label}
                </Button>
              );
            })}
          </div>
        </section>

        <section aria-label={t('myBoards.sheet.friendTitle')}>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-fg-muted">{t('myBoards.sheet.friendTitle')}</h3>
          <div className="relative mb-2">
            <Search aria-hidden className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
            <input
              type="search"
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
              placeholder={t('myBoards.sheet.search')}
              aria-label={t('myBoards.sheet.search')}
              className="h-11 w-full rounded-xl border border-border bg-bg pl-9 pr-3 text-sm text-fg outline-none placeholder:text-fg-muted focus:ring-1 focus:ring-brand"
            />
          </div>
          {visibili.length === 0 ? (
            <p className="py-2 text-sm text-fg-muted">{t('myBoards.sheet.noFriends')}</p>
          ) : (
            <div role="radiogroup" aria-label={t('myBoards.sheet.friendTitle')} className="flex flex-col">
              {visibili.map((a) => {
                const on = amico === a.userId;
                return (
                  <button
                    key={a.userId}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    data-testid={`sfida-amico-${a.userId}`}
                    onClick={() => {
                      haptics.selection();
                      setAmico(a.userId);
                    }}
                    className="flex min-h-12 items-center gap-3 rounded-xl px-1 py-1 text-left active:opacity-80"
                  >
                    <span
                      aria-hidden
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-sm font-bold text-fg"
                    >
                      {a.nome.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-bold text-fg">{a.nome}</span>
                      <span className="flex items-center gap-1.5 text-xs text-fg-muted">
                        {a.online && <span aria-hidden className="h-2 w-2 rounded-full bg-success" />}
                        {a.online ? t('myBoards.sheet.online') : t('myBoards.sheet.offline')}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
                        on ? 'border-brand' : 'border-border',
                      )}
                    >
                      {on && <span className="h-3 w-3 rounded-full bg-brand" />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Le differenze stanno SOPRA «Invia sfida»: chi manda le legge subito prima di mandare (ADR-0191 §6). */}
        <section aria-label={t('myBoards.sheet.diffTitle')} data-testid="sfida-differenze">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-fg-muted">{t('myBoards.sheet.diffTitle')}</h3>
          <ul className="flex flex-col gap-1.5 rounded-2xl bg-bg p-3 text-sm text-fg">
            {scenario.differences.map((d) => (
              <li key={d} data-diff={d} className="flex gap-2">
                <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                <span className="min-w-0 break-words">
                  {t(DIFFERENZA[d], {
                    side: t(`myBoards.side.${tira}`),
                    scoreW: position.match.scoreW,
                    scoreB: position.match.scoreB,
                    length: position.match.length,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <div className="sticky bottom-0 -mx-4 bg-bg-secondary px-4 pb-1 pt-2">
          <Button
            fullWidth
            peso="bold"
            data-testid="sfida-invia"
            disabled={!pronto}
            onClick={() => {
              if (amico === null || tempo === null) return;
              haptics.impact('light');
              onInvia({ positionId: position.id, color: colore, gameModeKey: tempo, friendUserId: amico });
            }}
            className="min-h-12 rounded-2xl text-base"
          >
            {t('myBoards.sheet.send')}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
